import { AxiosPromise } from 'axios';
import { ResponseDefault, ResponseUpdateLocker } from '@app/typings/services/service.d';
import { ProfileData } from './rider';
import { EvLockerStatus } from './evlockers';
import { NiuChargerStatus, UniversalChargerStatus } from './battery';
import { PaginationParams } from '@app/typings/default';
import { SensorInfo } from './logs';
import { CommandType, ModalType } from '@app/modules/winnonie/schema/status.enum';

export enum ValueType {
  NUMBER = 'number',
  BOOLEAN = 'boolean',
}

export enum ManageTableGroup {
  DEFAULT = 'default',
  LOCKER = 'locker',
  NIU = 'niu',
  UNIVERSAL = 'universal',
}

export enum PowerStatus {
  ON = 'on',
  OFF = 'off',
}

export enum StationType {
  NIU = 'niu',
  STROM = 'strom',
}

export enum StationStatus {
  ACTIVE = 'active',
  ALERT = 'alert',
  WARNING = 'warning',
  DELETED = 'deleted',
  INACTIVE = 'inactive',
  NO_POWER = 'no-power',
}

export enum StationAction {
  UPDATE_POWER_METER = 'UPDATE_POWER_METER',
  CANCEL = 'CANCEL',
  GET_STATUS = 'GET_STATUS',
  GET_UPDATE = 'GET_UPDATE',
  LOGIN = 'LOGIN',
  OCCUPIED = 'OCCUPIED',
  OPEN = 'OPEN',
  RETURN = 'RETURN',
  SEND_UPDATE = 'SEND_UPDATE',
  RESERVE = 'RESERVE',
  ACTIVATE = 'ACTIVATE',
  INACTIVATE = 'INACTIVATE',
  PRINT = 'PRINT',
  REGISTER = 'REGISTER',
  UNREGISTER = 'UNREGISTER',
  RESTART = 'RESTART',
}

export interface StationSearchParams {
  statuses?: string[];
  provinces?: string[];
  stationNo?: number;
  projectIds?: string[];
  types?: string[];
  onlineStatus?: boolean;
}

export interface FilterStation {
  filters?: StationSearchParams;
  pages: PaginationParams;
}

export interface SumChargerStatuses {
  niuChargerStatuses: { [key in NiuChargerStatus]: number };
  universalChargerStatuses: { [key in UniversalChargerStatus]: number };
}
export interface StationResponseData {
  stations: StationData[];
  sumChargerStatuses?: SumChargerStatuses;
  sumEvLockerStatuses?: { [key in EvLockerStatus]: number };
}
export interface StationData {
  id: string;
  projectId: string;
  profile: ProfileData;
  number: string;
  agentId: string;
  agent: string;
  status: string;
  sensors?: SensorConfig;
  totalEvLocker: number;
  evLockerConfig: EvLockerConfig;
  numberEmptyEvLocker: number;
  numberChargingEvLocker: number;
  numberChargedEvLocker: number;
  numberReservedEvLocker: number;
  numberNotReadyEvLocker: number;
  lockerStatus: EvLockerStatus;
  niuChargerStatus: NiuChargerStatus;
  universalChargerStatus: UniversalChargerStatus;
}

export interface WnnServiceManageTable {
  stationNo: boolean;
  station: boolean;
  type: boolean;
  address: boolean;
  district: boolean;
  province: boolean;
  location: boolean;
  status: boolean;
  flood: boolean;
  gas: boolean;
  temp: boolean;
  meter: boolean;
  activation: boolean;
  breaker: boolean;
  setting: boolean;
  total: boolean;
  locker: { [key in EvLockerStatus]: boolean };
  niuCharger: { [key in NiuChargerStatus]: boolean };
  universalCharger: { [key in UniversalChargerStatus]: boolean };
}

export interface EvLockerConfig {
  fullChargedThreshold: number;
  reservationTimeOut: number;
  nChargeCycleLimit: number;
  tempAlert: TempAlert;
}

export interface TempAlert {
  level1: number;
  level2: number;
}

export interface SensorConfig {
  floods: SensorInfo[];
  gases: SensorInfo[];
  temperatures: SensorInfo[];
  kWh: SensorInfo[];
}
export interface StationDataById {
  id: string;
  status: string;
  agentId: string;
  projectId: string;
  number: number;
  evLockerConfig: EvLockerConfig;
  sensors: SensorConfig;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateStationData {
  updatedAt: string;
  createdAt: string;
  status: string;
  agentId: string;
  projectId: string;
  number: number;
  _id: string;
}

export interface UpdateStationPowerData {
  station: {
    updatedAt: string;
    createdAt: string;
    status: string;
    agentId: string;
    projectId: string;
    number: number;
    _id: string;
  };
  mqttInfo: {
    id: string;
    successful: boolean;
  };
}

export interface UpdateStationDataDetail {
  updatedAt: string;
  createdAt: string;
  status: string;
  agentId: string;
  projectId: string;
  number: number;
  _id: string;
  fullChargedThreshold: number;
  reChargeThreshold: number;
  reservationTimeOut: number;
  nChargeCycleLimit: number;
}

interface fetchStationsResponse extends ResponseDefault {
  data: StationResponseData;
}

interface fetchStationByIdResponse extends ResponseDefault {
  data: StationDataById;
}

export interface FetchStationParams {
  offset?: number;
  limit?: number;
  agentName?: string;
  provinces?: string;
  winnonieNumber?: string;
  name?: string;
  statuses?: string;
}

export interface UpdateStationParams {
  status?: string;
}

export interface UpdateStationDataDetailParams {
  status?: string;
  reChargeThresholdParam?: number;
  fullChargedThresholdParam?: number;
  reservationTimeOutParam?: number;
  nChargeCycleLimitParam?: number;
  tempAlert?: TempAlert;
}

export interface UpdateWithReasonParams {
  stationId: string;
  command: CommandType;
  reason: string;
}

export interface UpdateWithoutReasonParams {
  stationId: string | string[];
  status: string;
}

export interface OpenModalParams {
  type: ModalType;
  action?: CommandType;
  status?: StationStatus;
  value?: boolean;
}

export type FetchStations = (params: FilterStation) => AxiosPromise<fetchStationsResponse>;

export type FetchStationById = (stationId: string) => AxiosPromise<fetchStationByIdResponse>;

interface updateStationResponse extends ResponseDefault {
  data: UpdateStationData;
}
interface UpdateStationPowerResponse extends ResponseDefault {
  data: UpdateStationPowerData;
}
interface UpdateStationDataDetailResponse extends ResponseDefault {
  data: UpdateStationDataDetail;
}

export type UpdateStation = (
  stationId: string,
  data: UpdateStationParams,
) => AxiosPromise<updateStationResponse>;

export type UpdateStationDetail = (
  stationId: string,
  data: UpdateStationDataDetailParams,
) => AxiosPromise<UpdateStationDataDetailResponse>;

export type UpdateLocker = (
  stationId: string,
  command: string,
) => AxiosPromise<ResponseUpdateLocker>;
export type UpdateStationPower = (
  params: UpdateWithReasonParams,
) => AxiosPromise<UpdateStationPowerResponse>;
